import { useState, useRef, type KeyboardEvent } from 'react';
import { Icon } from '../components/Icon';

interface TagInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  /** Autocomplete suggestions (from GET /api/portal/tools). */
  suggestions: string[];
  readOnly?: boolean;
}

/**
 * Free-text tag input with typeahead.
 *
 * - All non-added suggestions are always shown.
 * - Typing dims non-matching chips and highlights the match substring in bold.
 * - The top prefix-match shows as ghost-text (grey suffix) inside the input.
 * - Tab accepts the ghost (or the top match when nothing is typed).
 * - Enter / comma commits the typed value as a free-text tag.
 * - Backspace on empty removes the last tag.
 */
export function TagInput({ tags, onChange, suggestions, readOnly = false }: TagInputProps) {
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const lower = input.toLowerCase();

  // All catalogue entries not yet added by the user.
  const available = suggestions.filter(
    (s) => !tags.some((t) => t.toLowerCase() === s.toLowerCase()),
  );

  // Subset that actually matches the current input — used to derive the ghost.
  const matching = available
    .filter((s) => input === '' || s.toLowerCase().includes(lower))
    .sort((a, b) => {
      if (input === '') return 0;
      const ai = a.toLowerCase().indexOf(lower);
      const bi = b.toLowerCase().indexOf(lower);
      if (ai !== bi) return ai - bi;
      return a.localeCompare(b);
    });

  // Ghost: top match whose name *starts with* what the user typed.
  const ghost =
    input.length > 0 && matching.length > 0 && matching[0].toLowerCase().startsWith(lower)
      ? matching[0]
      : null;
  const ghostSuffix = ghost ? ghost.slice(input.length) : '';

  function addTag(raw: string) {
    const val = raw.trim().replace(/,$/, '').trim();
    if (!val) return;
    if (tags.some((t) => t.toLowerCase() === val.toLowerCase())) return;
    onChange([...tags, val]);
  }

  function removeAt(index: number) {
    onChange(tags.filter((_, i) => i !== index));
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Tab') {
      const accept = ghost ?? (input.trim() === '' && matching.length > 0 ? matching[0] : null);
      if (accept) {
        e.preventDefault();
        addTag(accept);
        setInput('');
      }
    } else if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(input);
      setInput('');
    } else if (e.key === 'Backspace' && input === '' && tags.length > 0) {
      removeAt(tags.length - 1);
    }
  }

  if (readOnly) {
    return (
      <div className="flex min-h-[44px] flex-wrap items-center gap-1.5 rounded-[9px] border-[1.5px] border-line bg-panel px-2 py-1.5">
        {tags.length === 0 ? (
          <span className="text-sm text-muted">—</span>
        ) : (
          tags.map((tag, i) => (
            <span
              key={`${tag}-${i}`}
              className="flex items-center gap-1 rounded-[5px] bg-accent-light py-0.5 px-2.5 text-xs font-semibold text-accent"
            >
              {tag}
            </span>
          ))
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Tag chips + text input */}
      <div
        className="flex min-h-[44px] flex-wrap items-center gap-1.5 rounded-[9px] border-[1.5px] border-line bg-white px-2 py-1.5 focus-within:border-accent"
        onClick={() => inputRef.current?.focus()}
      >
        {tags.map((tag, i) => (
          <span
            key={`${tag}-${i}`}
            className="flex items-center gap-1 rounded-[5px] bg-accent-light py-0.5 pl-2.5 pr-1.5 text-xs font-semibold text-accent"
          >
            {tag}
            <button
              type="button"
              aria-label={`Remove ${tag}`}
              className="flex items-center text-accent opacity-55 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                removeAt(i);
              }}
            >
              <Icon name="closeXs" size={10} />
            </button>
          </span>
        ))}

        {/* Ghost-text wrapper — must be a block/flex container so absolute children are
            positioned correctly relative to the input area, not to an inline span origin. */}
        <div className="relative flex min-w-[120px] flex-1 items-center">
          <input
            ref={inputRef}
            aria-label="Add a tool"
            className="relative z-10 w-full border-none bg-transparent p-0 text-sm text-ink outline-none"
            placeholder={tags.length === 0 ? 'Type a tool and press Enter…' : ''}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {/* Ghost overlay — sits behind the input (z-0). The transparent copy of the
              typed text takes up exactly the same layout space, pushing the grey suffix
              to the correct horizontal position without relying on font-metric hacks. */}
          {ghostSuffix && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 z-0 flex items-center overflow-hidden whitespace-pre text-sm select-none"
            >
              <span style={{ color: 'transparent' }}>{input}</span>
              <span style={{ color: '#a3a3a3' }}>{ghostSuffix}</span>
            </div>
          )}
        </div>
      </div>

      <div className="mt-1 text-xs text-muted">
        {ghostSuffix
          ? 'Tab to accept. Enter or comma to add your own.'
          : 'Enter or comma to add. Tab picks the top suggestion. Backspace to remove last.'}
      </div>

      {/* Suggestion chips — always shows the full catalogue; dims non-matches when typing */}
      {available.length > 0 && (
        <div className="mt-3">
          <div className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-muted">
            Suggestions
          </div>
          <div className="flex flex-wrap gap-1.5">
            {available.map((s) => {
              const isMatch = input === '' || s.toLowerCase().includes(lower);
              const idx = isMatch && input ? s.toLowerCase().indexOf(lower) : -1;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    addTag(s);
                    setInput('');
                    inputRef.current?.focus();
                  }}
                  className={`rounded-[5px] border px-2.5 py-0.5 text-[0.73rem] font-medium transition-all ${
                    isMatch
                      ? 'border-line bg-panel text-muted hover:border-accent hover:bg-accent-light hover:text-accent'
                      : 'border-transparent bg-transparent text-muted/30 hover:border-line hover:text-muted/60'
                  }`}
                >
                  {idx >= 0 && input ? (
                    <>
                      {s.slice(0, idx)}
                      <strong className="font-bold text-ink">{s.slice(idx, idx + input.length)}</strong>
                      {s.slice(idx + input.length)}
                    </>
                  ) : (
                    s
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
